import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Database, Cloud, Shield, Zap, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { services } from '@/data/mock';

const ServicesSection = () => {
  const iconMap = {
    Target,
    Database,
    Cloud,
    Shield,
    Zap,
    Users
  };

  return (
    <section className="relative py-24 bg-white">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            What We <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Do</span>
          </h2>
          <p className="text-slate-500 text-lg">
            Focused consulting services designed for measurable outcomes
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = iconMap[service.icon] || Target;
            return (
              <Link
                key={service.id}
                to={`/services/${service.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 hover:border-cyan-400/50 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500 bg-white"
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={service.image}
                    alt={`${service.title} - ElitePlus IT services UAE`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover opacity-5 group-hover:opacity-10 transform group-hover:scale-110 transition-all duration-700"
                  />
                </div>

                {/* Content */}
                <div className="relative p-8 min-h-[320px] flex flex-col">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Text */}
                  <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-cyan-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed flex-grow">
                    {service.shortDescription}
                  </p>

                  {/* Arrow */}
                  <div className="mt-6 flex items-center gap-2 text-cyan-500 font-medium text-sm">
                    <span className="transform group-hover:translate-x-2 transition-transform duration-300">
                      Learn More
                    </span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link to="/services">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 font-medium px-8 transition-all duration-300"
            >
              View All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
